import { Injectable } from "@angular/core";
import { BaseApiService } from './base-api.service';
import { Observable } from 'rxjs';
import { WeeklyData } from "../../dto/rod-weekly-summary.dto";
import { DashboardEnrichmentSummaryResponseDto } from "../../dto/dashboard-enrichment-summary-response.dto";
import { DashboardExportSummaryResponseDto } from "../../dto/dashboard-export-summary-response.dto";
import { DashboardExportsSummaryTypeEnum } from "../../enums/dashboard-exports-summary-type.enum";
import { map } from "rxjs/operators";
import { DashboardVisitorIntelAllWeeklyVisitedCountsResponseDto, DashboardVisitorIntelWeeklyExportedResponseDto, GrowthSignalAgentDashboardListDto, IcpAgentDashboardListDto, IntentAgentDashboardListDto, IntenttCompaniesSurgesCount, IntentWeeklySummaryDto, SearchWeeklySummaryDto, SessionsWeeklySummaryDto, ShowRevDriverCard, IcpMonthlySummaryDto, IcpSurgedCompaniesDto, GrowthSignalsMonthlySummaryResponseDto, GrowthSignalsCombinedSummaryResponseDto, GrowthSignalsMonthlyExportSummaryResponseDto } from "../../dto";
import { DashboardVisitorIntelWeeklySummaryResponseDto } from "../../dto/dashboard-visitor-intel-weekly-summary-response.dto";
import { DashboardVisitorIntelAgentsResponseDto } from "../../dto/dashboard-visitor-intel-agents-response.dto";
import { IntentSurgedCompaniesDto } from "../../dto";
import { NewsAutomationAgentsCount, DashboardNewsAgentsAutomationResponseDto } from "../../dto/dashboard-news-agents-response.dto";
import { DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto } from "../../dto/dashboard-visitor-intel-weekly-visited-companies-count.dto";
import { DashboardNewsAutomationsWeeklyArticlesCount, DashboardNewsAutomationsWeeklyArticlesCountResponseDto } from "../../dto/dashboard-news-automations-response.dto";

@Injectable({
    providedIn: 'root',
})

export class DashboardApiService {
    private readonly url: string = "service/success-center/dashboard";

    constructor(
        private readonly baseApiService: BaseApiService,
    ) {
    }

    getRODWeeklySummary(includeAllUsers: boolean = true): Observable<WeeklyData[]> {
        return this.baseApiService.get<WeeklyData[]>(`${this.url}/rod-weekly-summary`, {params: {includeAllUsers: includeAllUsers}, showError: true});
    }

    getEnrichmentSummary(): Observable<DashboardEnrichmentSummaryResponseDto> {
        return this.baseApiService.get<DashboardEnrichmentSummaryResponseDto>(`${this.url}/enrichment-summary`, {showError: true});
    }

    getAllExportSummary(exportType?: DashboardExportsSummaryTypeEnum): Observable<DashboardExportSummaryResponseDto> {
    const exportSummaryUrl = `${this.url}/export-summary`;
        if(exportType) {
            return this.baseApiService.get<DashboardExportSummaryResponseDto>(exportSummaryUrl, {params: {exportedTo: exportType}, showError: true});
        } else {
            return this.baseApiService.get<DashboardExportSummaryResponseDto>(exportSummaryUrl, {showError: true});
        }
    }

    showRevDriverCard(): Observable<boolean> {
        return this.baseApiService.get<ShowRevDriverCard>(`${this.url}/rev-driver/show-card`, {showError: true}).pipe(map(response => response.show_card));
    }

    getCompaniesSurgesCount(): Observable<IntenttCompaniesSurgesCount[]> {
        return this.baseApiService.get<IntenttCompaniesSurgesCount[]>(`${this.url}/intent/topic/companies-surges/count`)
    }

    getIntentAgents(): Observable<IntentAgentDashboardListDto[]> {
        return this.baseApiService.get<IntentAgentDashboardListDto[]>(`${this.url}/intent/agents`)
    }

    getAdminEmails(): Observable<string[]> {
        return this.baseApiService.get<string[]>(`${this.url}/admin-emails`);
    }
    
    getVisitorIntelWeeklyCompaniesCount(): Observable<DashboardVisitorIntelWeeklySummaryResponseDto[]> {
        return this.baseApiService.get<DashboardVisitorIntelWeeklySummaryResponseDto[]>(`${this.url}/visitor/intel/companies-count`, {showError: true});
    }

    getVisitorIntelAgentsCount(): Observable<DashboardVisitorIntelAgentsResponseDto[]> {
        return this.baseApiService.get<DashboardVisitorIntelAgentsResponseDto[]>(`${this.url}/visitor/intel/agents`, {showError: true});
    }

    getSurgedCompanies(automationId: number): Observable<IntentSurgedCompaniesDto> {
        return this.baseApiService.get<IntentSurgedCompaniesDto>(`${this.url}/intent/report/surged-companies`, { params: { automationId } });
    }

    getICPSurgedCompanies(automationId: number): Observable<IcpSurgedCompaniesDto> {
        return this.baseApiService.get<IcpSurgedCompaniesDto>(`${this.url}/icp/report/surged-companies`, { params: { automationId } });
    }

    getConfiguredNewsAgents(includeAllUsers: boolean = false): Observable<NewsAutomationAgentsCount[]> {
        return this.baseApiService.get<DashboardNewsAgentsAutomationResponseDto>(`${this.url}/news/automations`, {params: {includeAllUsers: includeAllUsers}, showError: true}).pipe(map(response => response.news_automations));
    }

    getVisitorIntelWeeklyVisitedCompaniesCount(automationId?: number): Observable<DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto> {
        const params = automationId ? { automationId } : null;
        return this.baseApiService.get<DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto>(
            `${this.url}/visitor/intel/weekly/visited-companies-count`,
            { params, showError: true }
        );
    }

    getIntentWeeklySummary(automationId?: number): Observable<IntentWeeklySummaryDto> {
        return this.baseApiService.get<IntentWeeklySummaryDto>(`${this.url}/intent/monthly-export-summary`, automationId ?{ params: { automationId } } : null);
    }

    getVisitorIntelWeeklyExportedCount(automationId?: number): Observable<DashboardVisitorIntelWeeklyExportedResponseDto> {
        const params = automationId ? { automationId } : null;
        return this.baseApiService.get<DashboardVisitorIntelWeeklyExportedResponseDto>(`${this.url}/visitor/intel/weekly/exported-count`, {params, showError: true});
    }

    getAllNewsArticlesWeeklyData(includeAllUsers: boolean = true): Observable<DashboardNewsAutomationsWeeklyArticlesCount[]> {
        return this.baseApiService.get<DashboardNewsAutomationsWeeklyArticlesCountResponseDto>(`${this.url}/news/articles-count`, {params: {includeAllUsers: includeAllUsers}, showError: true}).pipe(map(response => response.weekly_counts));
    }

    getSelectedAgentNewsArticlesWeeklyData(automationId?: number): Observable<DashboardNewsAutomationsWeeklyArticlesCount[]> {
        return this.baseApiService.get<DashboardNewsAutomationsWeeklyArticlesCountResponseDto>(`${this.url}/news/automations/${automationId}/articles-count`, {showError: true}).pipe(map(response => response.weekly_counts));
    }

    getAllVisitorIntelWeeklyVisitedExportedCount(): Observable<DashboardVisitorIntelAllWeeklyVisitedCountsResponseDto> {
        return this.baseApiService.get<DashboardVisitorIntelAllWeeklyVisitedCountsResponseDto>(`${this.url}/visitor/intel/weekly/visited-exported-count`, {showError: true});
    }

    getIntentMonthlyReportSummary(automationId?: number): Observable<IntentWeeklySummaryDto> {
        return this.baseApiService.get<IntentWeeklySummaryDto>(`${this.url}/intent/monthly-report-summary`, automationId ? { params: { automationId } } : null);
    }

    getIntentMonthlyCombinedSummary(automationId?: number): Observable<IntentWeeklySummaryDto> {
        return this.baseApiService.get<IntentWeeklySummaryDto>(`${this.url}/intent/monthly-combined-summary`, automationId ? { params: { automationId } } : null);
    }

    getGrowthSignalAgents(): Observable<GrowthSignalAgentDashboardListDto[]> {
        return this.baseApiService.get<GrowthSignalAgentDashboardListDto[]>(`${this.url}/signal/agents`, { showError: true });
    }

    getGrowthSignalsMonthlyCombinedSummary(): Observable<GrowthSignalsCombinedSummaryResponseDto> {
        return this.baseApiService.get<GrowthSignalsCombinedSummaryResponseDto>(`${this.url}/signal/monthly-combined-summary`, { showError: true });
    }

    getGrowthSignalMonthlyExportSummary(signalId?: number): Observable<GrowthSignalsMonthlyExportSummaryResponseDto> {
        return this.baseApiService.get<GrowthSignalsMonthlyExportSummaryResponseDto>(`${this.url}/signal/monthly-export-summary`, signalId ?{ params: { signalId } } : null);
    }

    getGrowthSignalsMonthlyReportSummary(signalId?: number): Observable<GrowthSignalsMonthlySummaryResponseDto> {
        return this.baseApiService.get<GrowthSignalsMonthlySummaryResponseDto>(`${this.url}/signal/monthly-report-summary`, signalId ? { params: { signalId }, showError: true } : { showError: true });
    }
    
    getSessionSummary(): Observable<SessionsWeeklySummaryDto[]> {
        return this.baseApiService.get<SessionsWeeklySummaryDto[]>(`${this.url}/session/weekly-summary`);
    }

    getSearchSummary(): Observable<SearchWeeklySummaryDto[]> {
        return this.baseApiService.get<SearchWeeklySummaryDto[]>(`${this.url}/search/weekly-summary`);
    }

    getIcpAgents(): Observable<IcpAgentDashboardListDto[]> {
        return this.baseApiService.get<IcpAgentDashboardListDto[]>(`${this.url}/icp/agents`);
    }

    getIcpMonthlyReportSummary(automationId?: number): Observable<IcpMonthlySummaryDto> {
        return this.baseApiService.get<IcpMonthlySummaryDto>(`${this.url}/icp/monthly-report-summary`, automationId ? { params: { automationId } } : null);
    }

    getIcpMonthlyExportSummary(automationId?: number): Observable<IcpMonthlySummaryDto> {
        return this.baseApiService.get<IcpMonthlySummaryDto>(`${this.url}/icp/monthly-export-summary`, automationId ? { params: { automationId } } : null);
    }

    getIcpMonthlyCombinedSummary(automationId?: number): Observable<IcpMonthlySummaryDto> {
        return this.baseApiService.get<IcpMonthlySummaryDto>(`${this.url}/icp/monthly-combined-summary`, automationId ? { params: { automationId } } : null);
    }
}
